import {round} from "../../../../../utils/math";
import {OriginalRevenueOperationalMetricValidKey, UnitType, UnitTypeScheduleValue, VersionType} from "../../../../../__generated__/generated_types";
import { AvailableMetrics, FullUnitTypeData, FullUnitTypeDataCalcInput, MetricArrays, MetricSingleValues, metricToLabelMap } from '../components/CombinedMetricsTable';
import {CompoundSimpleHotRow, SimpleHoTCellObject, SimpleHoTCellType, SimpleHoTChange, SimpleHoTRow} from "../../../../admin/line-item-management/SimpleHoTWrapper";
import {IBatchUpsertInput} from "../../hooks/useUTSVOperationalMetrics";
import {getVersionLabel, IUTSVUnitType} from "../../utils/operational-metric-helpers";
import {Property} from "../../../../../contexts/properties/logic";


const PRECISION = 5;

export function colPropToMonth(prop: string | number): number {
    const parts = String(prop).split(".");
    const monthIndex = parts.pop();
    return Number(monthIndex) ?? 0;
}

function getMoveIns(occupancyCount: number, prevOccupancyCount: number, moveOutCount: number): number {
    const result = occupancyCount - (prevOccupancyCount - moveOutCount);
    return round(result, PRECISION);
}

export function addToArray(sumArr: number[], toAdd: number[]): number[] {
    const result = [];
    const minLen = Math.min(sumArr.length, toAdd.length);
    for(let i = 0; i < minLen; i++) {
        result.push(sumArr[i]! + toAdd[i]!);
    }
    return result;
}

export function divideArray(arr: number[], divisor: number): number[] {
    if(divisor === 0) {
        return new Array(arr.length).fill(0);
    }
    return arr.map(a => +((a / divisor).toFixed(2)));
}

export function countToRate(count: number, total: number): number {
    if (total == 0) {
        return 0;
    }
    const result = (count / total) * 100;
    return round(result, 5);
}

export function getExpirationPercentOfUnits(expirations: number, unitTypeCount: number): number | null {
    if (!unitTypeCount) {
        return null;
    }
    return countToRate(expirations, unitTypeCount);
}

export function rateToCount(rate: number, total: number): number {
    const result = (rate / 100) * total;
    return round(result, 5);
}

export function getRatesAsCounts(rates: number[], totals: number[]): number[] {
    return rates.map((x, i) => rateToCount(x, (totals[i] ?? 0)));
}

export function getCountsAsRates(counts: number[], totals: number[]): number[] {
    return counts.map((x, i) => countToRate(x, (totals[i] ?? 0)));
}

// transform a rate so it represents the nearest whole unit
// rate arg should be in format 95.23, not .9523
export function rateToWholeUnitRate(rate: number, total: number): number {
    if (total === 0) {
        return rate;
    }
    const roundedCount = Math.round((rate / 100) * total);
    const roundedRate = (roundedCount / total) * 100;
    return roundedRate;
}

export function buildTableRowValues(
    builtUnitData: FullUnitTypeData,
    visibleMetrics: Set<AvailableMetrics>,
): {metricRows: SimpleHoTCellObject[], extraData: FullUnitTypeData} {
    const values: SimpleHoTCellObject[] = [];
    const extraData: FullUnitTypeData = {
        unitTypeName: builtUnitData.unitTypeName,
        unitTypeCount: builtUnitData.unitTypeCount,
        previousVersionLastMonthData: builtUnitData.previousVersionLastMonthData,
        versionType: builtUnitData.versionType,
        year: builtUnitData.year,
        counts: {
            leaseExpirationCount: Array(12).fill(0),
            renewalRatio: Array(12).fill(0),
            moveOutRate: Array(12).fill(0),
            occupancyRate: Array(12).fill(0),
            moveInCount: Array(12).fill(0),
        },
        rates: {
            leaseExpirationCount: Array(12).fill(0),
            renewalRatio: Array(12).fill(0),
            moveOutRate: Array(12).fill(0),
            occupancyRate: Array(12).fill(0),
            moveInCount: Array(12).fill(0),
        },
        countAverages: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        rateAverages: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        countTotals: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        rateTotals: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        }
    };
    for (const [metricKey, metricLabel] of Array.from(metricToLabelMap.entries())) {
        extraData.counts[metricKey] = builtUnitData.counts[metricKey];
        extraData.rates[metricKey] = builtUnitData.rates[metricKey];
        extraData.countAverages[metricKey] = builtUnitData.countAverages[metricKey];
        extraData.rateAverages[metricKey] = builtUnitData.rateAverages[metricKey];
        extraData.countTotals[metricKey] = builtUnitData.countTotals[metricKey];
        extraData.rateTotals[metricKey] = builtUnitData.rateTotals[metricKey];

        // we don't show the moveOuts row, but we still need its values which is why we only skip it after updating extraData
        if (!visibleMetrics.has(metricKey) || metricKey === OriginalRevenueOperationalMetricValidKey.MoveOutRate) {
            continue;
        }

        values.push({
            unitTypeName: builtUnitData.unitTypeName,
            metricLabel: metricLabel,
            metricKey: metricKey,
            countValues: builtUnitData.counts[metricKey],
            rateValues: builtUnitData.rates[metricKey],
            countAverages: builtUnitData.countAverages[metricKey],
            rateAverages: builtUnitData.rateAverages[metricKey],
            countTotals: builtUnitData.countTotals[metricKey],
            rateTotals: builtUnitData.rateTotals[metricKey],
        });
    }
    return {metricRows: values, extraData};
}

export function buildFullUnitTypeData(
    unitType: FullUnitTypeDataCalcInput,
    calculateBasedOnRates: boolean,
): FullUnitTypeData {
    let pctRenewals = unitType.renewalRatio;
    let pctMoveOuts = unitType.moveOutRate;
    let pctOccupancy = unitType.occupancyRate;

    // if the user is in 'counts' mode, then the values coming in are counts, not percents,
    // and we need to recalculate rates based on the count values before summarizing everything
    // expirations are always counts, regarldess of mode, so we can use those as-is
    if (!calculateBasedOnRates) {
        pctRenewals = getCountsAsRates(unitType.renewalRatio, unitType.leaseExpirationCount);
        pctMoveOuts = getCountsAsRates(unitType.moveOutRate, unitType.leaseExpirationCount);
        pctOccupancy = getCountsAsRates(unitType.occupancyRate, Array(12).fill(unitType.unitTypeCount));
    }

    const result: FullUnitTypeData = {
        unitTypeName: unitType.unitTypeName,
        unitTypeCount: unitType.unitTypeCount,
        previousVersionLastMonthData: unitType.previousVersionLastMonthData,
        versionType: unitType.versionType,
        year: unitType.year,
        rates: {
            leaseExpirationCount: unitType.leaseExpirationCount,
            renewalRatio: pctRenewals,
            moveOutRate: pctMoveOuts,
            occupancyRate: pctOccupancy,
            moveInCount: Array(12).fill(0),
        },
        counts: {
            leaseExpirationCount: unitType.leaseExpirationCount,
            renewalRatio: getRatesAsCounts(pctRenewals, unitType.leaseExpirationCount),
            moveOutRate: getRatesAsCounts(pctMoveOuts, unitType.leaseExpirationCount),
            occupancyRate: getRatesAsCounts(pctOccupancy, Array(12).fill(unitType.unitTypeCount)),
            moveInCount: Array(12).fill(0),
        },
        countAverages: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        rateAverages: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        countTotals: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        rateTotals: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        }
    };

    calculateMoveIns(result);
    calculateAveragesAndTotals(result);

    return result;
}

function calculateAveragesAndTotals(unitTypeData: FullUnitTypeData): FullUnitTypeData {
    unitTypeData.countAverages.leaseExpirationCount = unitTypeData.counts.leaseExpirationCount.average();
    unitTypeData.countAverages.renewalRatio = unitTypeData.counts.renewalRatio.sum() / 12;
    unitTypeData.countAverages.moveOutRate = unitTypeData.counts.moveOutRate.sum() / 12;
    unitTypeData.countAverages.occupancyRate = unitTypeData.counts.occupancyRate.sum() / 12;
    unitTypeData.countAverages.moveInCount = unitTypeData.counts.moveInCount.average();
    unitTypeData.rateAverages.leaseExpirationCount = unitTypeData.counts.leaseExpirationCount.average();
    unitTypeData.rateAverages.renewalRatio = countToRate(unitTypeData.counts.renewalRatio.sum(), unitTypeData.counts.leaseExpirationCount.sum());
    unitTypeData.rateAverages.moveOutRate = countToRate(unitTypeData.counts.moveOutRate.sum(), unitTypeData.counts.leaseExpirationCount.sum());
    unitTypeData.rateAverages.occupancyRate = countToRate(unitTypeData.counts.occupancyRate.sum(), unitTypeData.unitTypeCount * 12);
    unitTypeData.rateAverages.moveInCount = unitTypeData.counts.moveInCount.average();

    // I kind of feel like totals column doesnt make sense for rates, so they're just always count totals
    unitTypeData.countTotals.leaseExpirationCount = unitTypeData.counts.leaseExpirationCount.sum();
    unitTypeData.countTotals.renewalRatio = unitTypeData.counts.renewalRatio.sum();
    unitTypeData.countTotals.moveOutRate = unitTypeData.counts.moveOutRate.sum();
    unitTypeData.countTotals.occupancyRate = unitTypeData.counts.occupancyRate.sum();
    unitTypeData.countTotals.moveInCount = unitTypeData.counts.moveInCount.sum();
    unitTypeData.rateTotals.leaseExpirationCount = unitTypeData.counts.leaseExpirationCount.sum();
    unitTypeData.rateTotals.renewalRatio = unitTypeData.counts.renewalRatio.sum();
    unitTypeData.rateTotals.moveOutRate = unitTypeData.counts.moveOutRate.sum();
    unitTypeData.rateTotals.occupancyRate = unitTypeData.counts.occupancyRate.sum();
    unitTypeData.rateTotals.moveInCount = unitTypeData.counts.moveInCount.sum();

    return unitTypeData;
}

export function buildSummaryRow(unitTypeData: FullUnitTypeData[], property: Property): FullUnitTypeData {
    const firstUnitType = unitTypeData[0];
    let label = "Summary";
    let localVersionType = VersionType.Reforecast;
    let localYear = 0;
    if (firstUnitType) {
        const {versionType, year} = firstUnitType;
        localVersionType = versionType;
        localYear = year;
        label = getVersionLabel(versionType, year, property);
    }
    const result: FullUnitTypeData = {
        unitTypeName: label,
        unitTypeCount: 0,
        previousVersionLastMonthData: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        versionType: localVersionType,
        year: localYear,
        rates: {
            leaseExpirationCount: Array(12).fill(0),
            renewalRatio: Array(12).fill(0),
            moveOutRate: Array(12).fill(0),
            occupancyRate: Array(12).fill(0),
            moveInCount: Array(12).fill(0),
        },
        counts: {
            leaseExpirationCount: Array(12).fill(0),
            renewalRatio: Array(12).fill(0),
            moveOutRate: Array(12).fill(0),
            occupancyRate: Array(12).fill(0),
            moveInCount: Array(12).fill(0),
        },
        countAverages: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        rateAverages: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        countTotals: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        },
        rateTotals: {
            leaseExpirationCount: 0,
            renewalRatio: 0,
            moveOutRate: 0,
            occupancyRate: 0,
            moveInCount: 0,
        }
    };

    for (const unitType of unitTypeData) {
        result.unitTypeCount += unitType.unitTypeCount;

        result.counts.leaseExpirationCount = addToArray(result.counts.leaseExpirationCount, unitType.counts.leaseExpirationCount);
        result.counts.renewalRatio = addToArray(result.counts.renewalRatio, unitType.counts.renewalRatio);
        result.counts.moveOutRate = addToArray(result.counts.moveOutRate, unitType.counts.moveOutRate);
        result.counts.occupancyRate = addToArray(result.counts.occupancyRate, unitType.counts.occupancyRate);
        result.counts.moveInCount = addToArray(result.counts.moveInCount, unitType.counts.moveInCount);
    }

    result.rates.leaseExpirationCount = result.counts.leaseExpirationCount;
    result.rates.renewalRatio = getCountsAsRates(result.counts.renewalRatio, result.counts.leaseExpirationCount);
    result.rates.moveOutRate = getCountsAsRates(result.counts.moveOutRate, result.counts.leaseExpirationCount);
    result.rates.occupancyRate = getCountsAsRates(result.counts.occupancyRate, Array(12).fill(result.unitTypeCount));
    result.rates.moveInCount = result.counts.moveInCount;

    calculateAveragesAndTotals(result);

    return result;
}

/**
 * Use the data passed back from the SimpleHotWrapper onChange callback to calculate the new summarized data for a unit type
 */
export function calculateChangesFromTable(
    rowData: SimpleHoTRow,
    changedRows: SimpleHoTRow[],
    changedCols: number[],
    showAsRates: boolean,
    versionType: VersionType,
    year: number,
): FullUnitTypeDataCalcInput | null {
    const extraData = rowData?.extraData;
    if (!extraData) {
        return null;
    }
    const name = extraData["unitTypeName"] as string;
    const unitCount = extraData["unitTypeCount"] as number;
    const keyForValues = (showAsRates ? extraData["rates"] : extraData["counts"]) as MetricArrays;
    const expirations = keyForValues.leaseExpirationCount;
    const renewals = keyForValues.renewalRatio;
    const moveOuts = renewals.map((renewal, monthIndex) => {
        if (changedRows.includes(rowData) && changedCols.includes(monthIndex)) {
            return showAsRates ? 100 - renewal : (expirations[monthIndex] ?? 0) - renewal;
        }
        return keyForValues.moveOutRate[monthIndex] ?? 0;
    });
    const occupancy = keyForValues.occupancyRate;
    const moveIns = keyForValues.moveInCount;
    const previousVersionLastMonthData = (extraData["previousVersionLastMonthData"] ?? 0) as MetricSingleValues;

    return {
        unitTypeName: name,
        unitTypeCount: unitCount,
        leaseExpirationCount: expirations,
        renewalRatio: renewals,
        moveOutRate: moveOuts,
        occupancyRate: occupancy,
        moveInCount: moveIns,
        previousVersionLastMonthData: previousVersionLastMonthData,
        versionType: versionType,
        year: year,
    };
}

function calculateMoveIns(unitTypeData: FullUnitTypeData): FullUnitTypeData {
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const occupiedUnits = unitTypeData.counts.occupancyRate[monthIndex] ?? 0;
        const prevOccupiedUnits = monthIndex === 0 ? rateToCount(unitTypeData.previousVersionLastMonthData.occupancyRate, unitTypeData.unitTypeCount) : unitTypeData.counts.occupancyRate[monthIndex - 1];
        const moveOutCount = unitTypeData.counts.moveOutRate[monthIndex] ?? 0;
        const moveInCount = getMoveIns(occupiedUnits, prevOccupiedUnits ?? 0, moveOutCount);
        unitTypeData.counts.moveInCount[monthIndex] = moveInCount;
        unitTypeData.rates.moveInCount[monthIndex] = moveInCount;
    }
    return unitTypeData;
}

export function getFilteredHistoricalRows(historicalTableRows: CompoundSimpleHotRow[], visibleMetrics: Set<AvailableMetrics>): CompoundSimpleHotRow[] {
    const filteredHistoricalRows: CompoundSimpleHotRow[] = [];
    for (const historical of historicalTableRows) {
        const filteredChildren = historical?.children?.map(child => {
            return {
                ...child,
                values: child.values.filter(x => visibleMetrics.has(x["metricKey"] as AvailableMetrics)),
            }
        });
        filteredHistoricalRows.push({
            ...historical,
            values: historical.values.filter(x => visibleMetrics.has(x["metricKey"] as AvailableMetrics)),
            children: filteredChildren,
        });
    }
    return filteredHistoricalRows;
}

/**
 * Take the current array of summary row changes and adds changes to corresponding unit type rows
 * and returns an array of any changed CompoundSimpleHotRows
 */
export function updateUnitTypeCellsAfterSummaryRowChange(
    currentChanges: SimpleHoTChange[],
    unitTypeRow: CompoundSimpleHotRow,
    summaryRowChanges: SimpleHoTChange[],
    showAsRates: boolean,
): CompoundSimpleHotRow[] {
    const updatedUnitTypeRows: CompoundSimpleHotRow[] = [];
    for (const summaryRowChange of summaryRowChanges) {
        if (summaryRowChange.compoundRowValuesIndex === undefined || !Array.isArray(unitTypeRow.values)) continue;
        const metricRow = unitTypeRow.values[summaryRowChange.compoundRowValuesIndex] as SimpleHoTCellObject;
        if (showAsRates) {
            const rateValues = metricRow["rateValues"] as number[];
            const col = colPropToMonth(summaryRowChange.col);
            const previousValue = String(rateValues[col]);
            rateValues[col] = Number(summaryRowChange.newValue ?? 0);
            currentChanges.push({
                row: unitTypeRow,
                compoundRowValuesIndex: summaryRowChange.compoundRowValuesIndex,
                previousValue: previousValue,
                newValue: Number(summaryRowChange.newValue ?? 0),
                col: summaryRowChange.col,
                rowId: summaryRowChange.rowId
            });
        } else {
            const countValues = metricRow["countValues"] as number[];
            const col = colPropToMonth(summaryRowChange.col);
            const previousValue = String(countValues[col]);
            countValues[col] = Number(summaryRowChange.newValue ?? 0);
            currentChanges.push({
                row: unitTypeRow,
                compoundRowValuesIndex: summaryRowChange.compoundRowValuesIndex,
                previousValue: previousValue,
                newValue: Number(summaryRowChange.newValue ?? 0),
                col: summaryRowChange.col,
                rowId: summaryRowChange.rowId
            });
        }
        updatedUnitTypeRows.push(unitTypeRow);
    }
    return updatedUnitTypeRows;
}

export function buildAPIPayloadFromChanges(
    changes: SimpleHoTChange[],
    newData: FullUnitTypeDataCalcInput[],
    showAsRates: boolean,
): IBatchUpsertInput[] {
    const changesToWrite: Map<AvailableMetrics, IBatchUpsertInput[]> = new Map();

    for (const change of changes) {
        const compoundIndex = change.compoundRowValuesIndex;

        if (compoundIndex === undefined || !change.row.isCompound) continue;

        const metric = change.row.values[compoundIndex]?.["metricKey"] as AvailableMetrics;

        if (change.previousValue === change.newValue && metric !== OriginalRevenueOperationalMetricValidKey.RenewalRatio) continue;

        const unitTypeName = change.row?.extraData?.["unitTypeName"] as string;
        const monthIndex = colPropToMonth(change.col);
        const changedRowData = newData.find(x => x.unitTypeName == unitTypeName);
        const existingWrites = changesToWrite.get(metric);
        if (changedRowData && metric && unitTypeName) {
            const builtChange = buildFullUnitTypeData(changedRowData, showAsRates);
            const value = builtChange.rates[metric][monthIndex];

            if (value === undefined) {
                continue;
            }
            const payload = {
                fieldName: metric,
                unitTypeName,
                monthIndex,
                value: metric === OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount ? String(value) : String(value / 100.0),
            };

            if (existingWrites) {
                existingWrites.push(payload);
            } else {
                changesToWrite.set(metric, [payload]);
            }

            if (metric === OriginalRevenueOperationalMetricValidKey.RenewalRatio) {
                const moveOutValue = builtChange.rates[OriginalRevenueOperationalMetricValidKey.MoveOutRate][monthIndex];
                if (moveOutValue === undefined) {
                    continue;
                }
                const payload = {
                    fieldName: OriginalRevenueOperationalMetricValidKey.MoveOutRate,
                    unitTypeName,
                    monthIndex,
                    value: String(moveOutValue / 100.0),
                };
                const existingWrites = changesToWrite.get(OriginalRevenueOperationalMetricValidKey.MoveOutRate);
                if (existingWrites) {
                    existingWrites.push(payload);
                } else {
                    changesToWrite.set(OriginalRevenueOperationalMetricValidKey.MoveOutRate, [payload]);
                }
            }
        }
    }

    return Array.from(changesToWrite.values()).flat();
}

// this is just a stupid simple NaN check, but could be expanded on to have metric-specific validations,
// like no negative expirations, for instance
export function validateOccupancyManagerInput(value: SimpleHoTCellType): boolean {
    let inputIsValid = true;

    if (isNaN(Number(value))) {
        inputIsValid = false;
    }

    return inputIsValid;
}

// get the metric associated with a simplehotchange
function getChangeMetric(change: SimpleHoTChange): AvailableMetrics | undefined {
    const {row, compoundRowValuesIndex} = change;
    if (!row.isCompound || compoundRowValuesIndex === undefined) {
        return undefined;
    }
    return row.values[compoundRowValuesIndex]?.["metricKey"] as AvailableMetrics | undefined;
}

// figure out if we need to divide by the unit count, for occupancy, or the expiration count, for renewals and move outs
function getWholeUnitToDivideBy(
    change: SimpleHoTChange,
    metric: AvailableMetrics,
    monthIndex: number,
    allChanges: SimpleHoTChange[],
): number | undefined {
    const extraData = change.row.extraData;

    if (!extraData) {
        return undefined;
    }

    if (metric === OriginalRevenueOperationalMetricValidKey.OccupancyRate) {
        return extraData["unitTypeCount"] as number;
    }

    // if the change array contains an updated expiration count, we need to use that one instead of the existing expiration count
    const expirationChange = allChanges.find(x =>
        x.row === change.row &&
        colPropToMonth(x.col) === monthIndex &&
        getChangeMetric(x) === OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount);

    if (expirationChange) {
        return Number(expirationChange.newValue);
    }

    return (extraData["counts"] as MetricArrays | undefined)?.leaseExpirationCount[monthIndex];
}

// given an array of simplehotchanges transform them to rounded values
export function applyWholeUnitRounding(changes: SimpleHoTChange[], showAsRates: boolean): SimpleHoTChange[] {
    // renewals and move outs are calculated by expirations for that month, so if a change array contains
    // changes to expirations we need to round those first so they can be used later when finding the nearest
    // whole unit for renewals/move outs, rather than rely on the currently displayed expirations in the table
    for (const change of changes) {
        const metric = getChangeMetric(change);

        if (metric === OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount) {
            change.newValue = Math.round(Number(change.newValue));
        }
    }

    const transformedChanges = changes.map(change => {
        const metric = getChangeMetric(change);
        const monthIndex = colPropToMonth(change.col);
        const value = Number(change.newValue);

        const metricsToSkip = [OriginalRevenueOperationalMetricValidKey.MoveInCount, OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount];

        if (metric === undefined || !Number.isFinite(value) || monthIndex < 0 || monthIndex > 11 || metricsToSkip.includes(metric)) {
            return change;
        }

        // if we are in counts mode, you can just round the value
        if (!showAsRates) {
            return {...change, newValue: Math.round(value)};
        }

        const countToDivideBy = getWholeUnitToDivideBy(change, metric, monthIndex, changes);
        if (countToDivideBy === undefined || !Number.isFinite(countToDivideBy)) {
            return change;
        }

        return {...change, newValue: rateToWholeUnitRate(value, countToDivideBy)};
    });

    return transformedChanges;
}

// loop over every value in the given utsv data and round any non whole unit values, building an array
// of batch changes to pass back
export function roundAllValuesToWholeUnits(
    fullUnitData: FullUnitTypeData[],
    startMonthIndex: number | undefined,
    versionType: VersionType,
): IBatchUpsertInput[] {
    const changes: IBatchUpsertInput[] = [];

    if (startMonthIndex === undefined || startMonthIndex < 0 || startMonthIndex > 11) {
        startMonthIndex = 0;
    }

    for (const unitData of fullUnitData) {
        // make sure we aren't trying to update values for any period other than the current tab
        if (unitData.versionType !== versionType) {
            continue;
        }

        for (let monthIndex = startMonthIndex; monthIndex < 12; monthIndex++) {
            const expirations = unitData.counts.leaseExpirationCount[monthIndex] ?? 0;
            const currentRenewal = unitData.rates.renewalRatio[monthIndex] ?? 0;
            const currentMoveOut = unitData.rates.moveOutRate[monthIndex] ?? 0;
            const currentOccupancy = unitData.rates.occupancyRate[monthIndex] ?? 0;

            const roundedOccupancy = rateToWholeUnitRate(currentOccupancy, unitData.unitTypeCount);
            const roundedRenewal = rateToWholeUnitRate(currentRenewal, expirations);
            const roundedMoveOut = 100 - roundedRenewal;

            if (roundedRenewal !== currentRenewal) {
                changes.push({
                    fieldName: OriginalRevenueOperationalMetricValidKey.RenewalRatio,
                    unitTypeName: unitData.unitTypeName,
                    monthIndex: monthIndex,
                    value: String(roundedRenewal / 100.0),
                });
            }

            if (roundedMoveOut !== currentMoveOut) {
                changes.push({
                    fieldName: OriginalRevenueOperationalMetricValidKey.MoveOutRate,
                    unitTypeName: unitData.unitTypeName,
                    monthIndex: monthIndex,
                    value: String(roundedMoveOut / 100.0),
                });
            }

            if (roundedOccupancy !== currentOccupancy) {
                changes.push({
                    fieldName: OriginalRevenueOperationalMetricValidKey.OccupancyRate,
                    unitTypeName: unitData.unitTypeName,
                    monthIndex: monthIndex,
                    value: String(roundedOccupancy / 100.0),
                });
            }
        }
    }

    return changes;
}
